import './CreateNewProduction.scss';
import {Box, Typography} from '@mui/material';
import {useTranslation, Trans} from 'react-i18next';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import BasicInfo from './basic-info/BasicInfo';
import {useEffect, useState, useRef, useMemo} from 'react';
import {ArrStepper, Step} from '../../common/arr-stepper/ArrStepper';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {
  stepperItems,
  getStepKeyFromNumber,
  getStepConfig,
  ToastType,
  FormMode,
  STEP_KEY_BASIC_INFO,
  STEP_KEY_PRODUCTION_COMPONENTS,
  STEP_KEY_NUMBERING_AND_SORTING,
  STEP_KEY_DOCUMENT_SELECTION,
  STEP_KEY_PRIV_GUARD,
  STEP_KEY_DELIVERY_OPTIONS,
  STEP_KEY_SUMMARY_AND_PREVIEW,
  STEP_KEY_GENERATE,
  STEP_KEY_QUALITY_CONTROL_AND_CONFIRMATION,
  QUERY_PARAM_PRODUCTION_ID,
  ROUTE_PRODUCTION_HOME,
  ProductionStatus,
  ProductionCurrentStatusCode,
  ProductionWizardStepNumber,
  ModuleType,
} from './CreateNewProductionUtils';
import ProductionComponents from './production-components/ProductionComponents';
import NumberingSorting from './numbering-sorting/NumberingSorting';
import DocumentSelection from './document-selection/DocumentSelection';
import Generate from './generate/Generate';
import {useGetProductionBasicInfoInEditMode} from '../../../hooks/productions/useProductions';
import {useNavigate, useSearchParams, useLocation} from 'react-router-dom';
import StepWrapper from './common/step-wrapper/StepWrapper';
import {useMultiStepForm} from '../../../hooks/productions/useMultiStepForm';
import PrivGuard from './priv-guard/PrivGuard';
import SummaryAndPreview from './summary-preview/SummaryAndPreview';
import QualityControlAndConfirmation from './quality-control-and-confirmation/QualityControlAndConfirmation';
import DeliveryOptions from './delivery-options/DeliveryOptions';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrModal from '../../common/arr-modal/ArrModal';
import {setIsFieldsChanged} from '../../../redux/slice/productionSlice';

// Type for child component ref
type StepChildRef = {
  save: () => void;
  next: () => void;
};

const CreateNewProduction: React.FC = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Extract navigation state
  const navigationState = location.state as {
    securityGroupId?: number;
    productionSetId?: number;
    isProductionSet?: boolean;
  } | null;

  const [showToast, setShowToast] = useState<boolean>(false);
  const [mode, setMode] = useState<FormMode>(FormMode.CREATE);
  const [formStatus, setFormStatus] = useState<ToastType>(ToastType.Success);
  const [formMessage, setFormMessage] = useState<string | undefined>('');
  const [selectedSecurityGroupId, setSelectedSecurityGroupId] =
    useState<number>(0);
  const [selectedProductionSetId, setSelectedProductionSetId] =
    useState<number>(0);
  const [isProductionSet, setIsProductionSet] = useState<boolean>(true);
  const [steps, setSteps] = useState<Step[]>(stepperItems(true));
  const [productionId, setProductionId] = useState<number>(0);
  const [exportProductionId, setExportProductionId] = useState<number>(0);

  // New state for button control
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isUpdateNeeded, setIsUpdateNeeded] = useState<boolean>(false);

  const isFieldsChanged = useSelector(
    (state: RootState) => state.production.isFieldsChanged
  );

  // State to track initial step for edit mode
  const [initialStepKey, setInitialStepKey] =
    useState<string>(STEP_KEY_BASIC_INFO);

  // Ref to track if initial step has been set (to prevent re-navigation)
  const hasSetInitialStep = useRef<boolean>(false);

  // Ref to track if navigation state has been consumed and reset
  const hasConsumedNavigationState = useRef<boolean>(false);

  // Ref to communicate with child components
  const childRef = useRef<StepChildRef | null>(null);

  const encryptedProductionId =
    searchParams.get(QUERY_PARAM_PRODUCTION_ID) || '';

  // Convert existing stepperItems to stepDefinitions - memoized to prevent infinite loops
  const stepDefinitions = useMemo(
    () =>
      stepperItems(isProductionSet).map((s) => ({
        key: s.key,
        label: s.label,
        tooltipTitle: s.tooltipTitle,
        tooltip: s.tooltip,
      })),
    [isProductionSet]
  );

  const {
    currentStepKey,
    steps: multiStepSteps,
    goNext,
    goBack,
    goToStep,
  } = useMultiStepForm({
    stepDefinitions,
    initialStepKey: initialStepKey, // Use state-based initial step
    onStepChange: (key) => {
      // Sync with existing stepper state management
      setSteps((prev) =>
        prev.map((s) =>
          s.key === key
            ? {...s, state: 'current'}
            : s.key === currentStepKey
              ? {...s, state: 'completed'}
              : s
        )
      );
    },
  });
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const stepperWrapperRef = useRef<HTMLDivElement | null>(null);
  const [stepsMovedNext, setStepsMovedNext] = useState<Record<string, boolean>>(
    {}
  );
  const {
    data: getProductionBasicInfoInEditModeData,
    mutate: getProductionBasicInfoInEditMode,
  } = useGetProductionBasicInfoInEditMode();

  const isProductionLocked = useMemo(() => {
    let isInProgressOrCompleted =
      getProductionBasicInfoInEditModeData?.productionCurrentStatus ===
        ProductionCurrentStatusCode.INPROGRESS ||
      getProductionBasicInfoInEditModeData?.productionCurrentStatus ===
        ProductionCurrentStatusCode.COMPLETED;

    //Do not need to consider if step is last step and status is in progress from API consider as draft
    if (
      getProductionBasicInfoInEditModeData?.currentStep !==
        ProductionWizardStepNumber.GENERATE &&
      getProductionBasicInfoInEditModeData?.productionCurrentStatus ===
        ProductionCurrentStatusCode.INPROGRESS
    )
      isInProgressOrCompleted = false;

    const isReproductionInProgress =
      getProductionBasicInfoInEditModeData?.isReproductionInProgress ?? false;

    return isInProgressOrCompleted || isReproductionInProgress;
  }, [getProductionBasicInfoInEditModeData]);

  useEffect(() => {
    dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    setShowModal(false);
  }, []);

  useEffect(() => {
    let isUpdateNeeded = true;

    if (
      !isFieldsChanged &&
      getProductionBasicInfoInEditModeData?.currentStep ===
        ProductionWizardStepNumber.QUALITY_CONTROL_AND_CONFIRMATION &&
      productionId > 0
    ) {
      isUpdateNeeded = false;
    }
    setIsUpdateNeeded(isUpdateNeeded as boolean);
  }, [
    isFieldsChanged,
    currentIndex,
    productionId,
    getProductionBasicInfoInEditModeData,
  ]);

  useEffect(() => {
    if (!encryptedProductionId) {
      // Skip if we've already consumed and reset the navigation state
      if (hasConsumedNavigationState.current) {
        return;
      }

      // CREATE MODE: Must have navigation state
      if (
        navigationState?.securityGroupId &&
        navigationState?.productionSetId
      ) {
        // Extract and use the navigation state data
        setSelectedSecurityGroupId(navigationState.securityGroupId);
        setSelectedProductionSetId(navigationState.productionSetId);
        const isProdSet = navigationState.isProductionSet ?? true;
        setIsProductionSet(isProdSet);

        // Mark as consumed
        hasConsumedNavigationState.current = true;

        // Immediately reset/clear the navigation state
        // Replace current location with same path but no state
        navigate(location.pathname, {replace: true, state: null});
      } else {
        // No navigation state in create mode - redirect back
        navigate(ROUTE_PRODUCTION_HOME, {replace: true});
      }
    }
    // EDIT MODE: encryptedProductionId exists - data comes from API response
    // (handled in the other useEffect that processes getProductionBasicInfoInEditModeData)
  }, [navigationState, encryptedProductionId, navigate, location.pathname]);

  useEffect(() => {
    if (encryptedProductionId) {
      getProductionBasicInfoInEditMode(encryptedProductionId);
      setMode(FormMode.EDIT);
    }
  }, [searchParams, currentIndex]);

  useEffect(() => {
    if (encryptedProductionId && getProductionBasicInfoInEditModeData) {
      setSelectedSecurityGroupId(
        getProductionBasicInfoInEditModeData?.securityGroupId || 0
      );
      setSelectedProductionSetId(
        getProductionBasicInfoInEditModeData?.parentSetID || 0
      );

      // Determine isProductionSet from moduleType
      // If moduleType is 'production', it's a production set; otherwise it's an export set
      const moduleType =
        getProductionBasicInfoInEditModeData?.moduleType?.toLowerCase() || '';
      const isProductionSetFromModule =
        moduleType === ModuleType.PRODUCTION.toLocaleLowerCase();

      // Get exportProductionId from API response
      const exportProdId =
        getProductionBasicInfoInEditModeData?.exportProductionId
          ? Number(getProductionBasicInfoInEditModeData.exportProductionId)
          : 0;
      const prodId = getProductionBasicInfoInEditModeData?.productionId
        ? Number(getProductionBasicInfoInEditModeData.productionId)
        : 0;

      // Update state values (separate useEffect will handle steps update)
      setIsProductionSet(isProductionSetFromModule);
      setExportProductionId(exportProdId);
      setProductionId(prodId);

      // Set initial step based on currentStep from API (only once)
      if (
        getProductionBasicInfoInEditModeData?.currentStep !== undefined &&
        !hasSetInitialStep.current
      ) {
        const stepKey = getStepKeyFromNumber(
          getProductionBasicInfoInEditModeData.currentStep
        );
        setInitialStepKey(stepKey);
        goToStep(stepKey);
        hasSetInitialStep.current = true;
      }
    }
    //Make Save disabled if production is complete or in progress
    setIsSaveDisabled(isProductionLocked);
  }, [getProductionBasicInfoInEditModeData, goToStep, isProductionLocked]);

  // Separate useEffect to update steps when isProductionSet changes
  useEffect(() => {
    setSteps(stepperItems(isProductionSet));
  }, [isProductionSet]);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const currentStepEl =
        stepperWrapperRef.current?.querySelector<HTMLElement>(
          '[aria-current="step"]'
        );

      currentStepEl?.focus();
    });

    return () => window.cancelAnimationFrame(id);
  }, [currentIndex]);

  // Navigation handlers that call child component methods
  const handleSave = () => {
    childRef.current?.save();
  };

  const handleNext = () => {
    if (childRef.current) {
      childRef.current?.next();
      setStepsMovedNext((prev) => ({
        ...prev,
        [currentStepKey]: true,
      }));
    }
  };

  const handleBack = () => {
    if (isFieldsChanged) {
      setShowModal(true);
      return false;
    }
    // If we're on DeliveryOptions and exportProductionId > 0, skip PrivGuard
    backNavigation();
  };

  const backNavigation = () => {
    if (
      currentStepKey === STEP_KEY_DELIVERY_OPTIONS &&
      exportProductionId > 0
    ) {
      goToStep(STEP_KEY_DOCUMENT_SELECTION);
    } else {
      goBack();
    }
  };

  // Handle child state changes
  const handleChildStateChange = (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => {
    setIsSaveDisabled(state.saveDisabled);
    setIsNextDisabled(state.nextDisabled);
  };

  // Handle update needed state changes from child components
  const handleUpdateNeededChange = (value: boolean) => {
    setIsUpdateNeeded(value);
  };

  return (
    <>
      <Box className="new-production-container" ref={stepperWrapperRef}>
        <Box className="new-production-header">
          <Box className="new-production-title">
            <Typography
              tabIndex={0}
              className="typo-text headline3_size new-production-title-text"
              data-testid={`create-new-production-header`}
            >
              {isProductionSet
                ? t('productionsAndExports.createNewProduction.headerTitle')
                : t(
                    'productionsAndExports.createNewProduction.headerTitleExport'
                  )}
            </Typography>
            <ArrTooltipWrapper
              closeTooltipOnEscPress
              title={
                isProductionSet
                  ? t(
                      'productionsAndExports.createNewProduction.headerTooltip.title'
                    )
                  : t(
                      'productionsAndExports.createNewProduction.headerTooltipExport.title'
                    )
              }
              trigger={
                <InfoIcon
                  tabIndex={0}
                  fontSize="medium"
                  className="info-icon"
                />
              }
            >
              {isProductionSet ? (
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.headerTooltip.content"
                  components={{
                    br: <br data-testid={`create-new-production-br`} />,
                    strong: (
                      <strong data-testid={`create-new-production-strong`} />
                    ),
                    p: <p data-testid={`create-new-production-p`} />,
                    ul: <ul data-testid={`create-new-production-ul`} />,
                    li: <li data-testid={`create-new-production-li`} />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.headerTooltipExport.content"
                  components={{
                    br: <br data-testid={`create-new-production-br-btn`} />,
                    strong: (
                      <strong
                        data-testid={`create-new-production-strong-btn`}
                      />
                    ),
                    p: <p data-testid={`create-new-production-p-text`} />,
                    ul: <ul data-testid={`create-new-production-ul-btn`} />,
                    li: <li data-testid={`create-new-production-li-btn`} />,
                  }}
                />
              )}
            </ArrTooltipWrapper>
          </Box>
          <Box className="new-production-stepper">
            <ArrStepper
              readStep={true}
              steps={multiStepSteps}
              currentStepKey={currentStepKey}
              allowNonLinear={false}
              onStepChange={goToStep}
              dataTestId={'create-new-production'}
            />
          </Box>
        </Box>

        <StepWrapper
          steps={multiStepSteps}
          currentStepKey={currentStepKey}
          isSaveDisabled={isSaveDisabled || isProductionLocked}
          isNextDisabled={isNextDisabled}
          isBackDisabled={false}
          onSave={handleSave}
          onNext={handleNext}
          onBack={handleBack}
          onStepChange={goToStep}
          {...getStepConfig(currentStepKey)}
        >
          {currentStepKey === STEP_KEY_BASIC_INFO && (
            <BasicInfo
              ref={childRef}
              mode={mode}
              securityGroupId={selectedSecurityGroupId}
              productionSetId={selectedProductionSetId}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_PRODUCTION_COMPONENTS && (
            <ProductionComponents
              ref={childRef}
              stepsMovedNext={stepsMovedNext}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              exportProductionId={exportProductionId}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_NUMBERING_AND_SORTING && (
            <NumberingSorting
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              exportProductionId={exportProductionId}
              productionName={
                getProductionBasicInfoInEditModeData?.productionName || ''
              }
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_DOCUMENT_SELECTION && (
            <DocumentSelection
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              exportProductionId={exportProductionId}
              isProductionSet={isProductionSet}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              mode={mode}
              onStateChange={handleChildStateChange}
              skipToDeliveryOptions={() => goToStep(STEP_KEY_DELIVERY_OPTIONS)}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_PRIV_GUARD && (
            <PrivGuard
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              productionName={
                getProductionBasicInfoInEditModeData?.productionName || ''
              }
              isProductionSet={isProductionSet}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
              apiCurrentStep={getProductionBasicInfoInEditModeData?.currentStep}
            />
          )}
          {currentStepKey === STEP_KEY_DELIVERY_OPTIONS && (
            <DeliveryOptions
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              exportProductionId={exportProductionId}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              mode={mode}
              onStateChange={handleChildStateChange}
              skipToDocumentSelection={() =>
                goToStep(STEP_KEY_DOCUMENT_SELECTION)
              }
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_SUMMARY_AND_PREVIEW && (
            <SummaryAndPreview
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
              isUpdateNeeded={isUpdateNeeded}
              onUpdateNeededChange={handleUpdateNeededChange}
            />
          )}
          {currentStepKey === STEP_KEY_GENERATE && (
            <Generate
              ref={childRef}
              securityGroupId={selectedSecurityGroupId}
              productionSetId={selectedProductionSetId}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goNext={goNext}
              goBack={goBack}
              onStateChange={handleChildStateChange}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
            />
          )}
          {currentStepKey === STEP_KEY_QUALITY_CONTROL_AND_CONFIRMATION && (
            <QualityControlAndConfirmation
              ref={childRef}
              productionId={
                getProductionBasicInfoInEditModeData?.productionId || 0
              }
              isProductionSet={isProductionSet}
              productionName={
                getProductionBasicInfoInEditModeData?.productionName || ''
              }
              productionSetId={selectedProductionSetId}
              getStatus={(isShowToast, status, message) => {
                setShowToast(isShowToast);
                setFormStatus(status);
                setFormMessage(message);
              }}
              goBack={goBack}
              productionStatusCode={
                isProductionLocked ? ProductionStatus.INPROGRESS : ''
              }
            />
          )}
        </StepWrapper>
      </Box>
      {showToast && (
        <ArrToastNotification
          message={formMessage || ''}
          status={[formStatus]}
          setErrorIconAsAlert={formStatus !== ToastType.Success}
          onClose={() => setShowToast(false)}
        />
      )}
      {showModal && (
        <ArrModal
          showModal={showModal}
          ariaLabel={t('docRedactions.deleteRedactionModal.deleteRedaction')}
          title={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.title'
          )}
          ariaDescription={t(
            'docRedactions.deleteRedactionModal.sureWantToDelete'
          )}
          description={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.description'
          )}
          onClose={() => {
            setShowModal(false);
          }}
          autoFocusCloseButton={true}
          headerTitle={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.headerTitle'
          )}
          dataTestId="createnewprod-modal"
          buttons={[
            {
              id: 'no-button',
              label: t(
                'productionsAndExports.createNewProduction.common.unsavedChangesModal.btnCancel'
              ),
              dataTestId: 'cnp-no-button',
              onClick: () => {
                setShowModal(false);
              },
              variant: 'secondary',
              disabled: false,
            },
            {
              id: 'yes-button',
              label: t(
                'productionsAndExports.createNewProduction.common.unsavedChangesModal.btnLeave'
              ),
              dataTestId: 'cnp-yes-button',
              onClick: () => {
                setShowModal(false);
                dispatch(setIsFieldsChanged({isFieldsChanged: false}));
                backNavigation();
              },
              variant: 'primary',
              loading: false,
              disabled: false,
            },
          ]}
        />
      )}
    </>
  );
};
export default CreateNewProduction;
